import type { PageLogic } from "@patient-watch/forms";
import { parsePageLogic, validatePageLogic } from "@patient-watch/forms";
import { useInputsWithChoices } from "@/features/dictionaries/useInputsWithChoices";
import type { FormBuilderData } from "@/features/forms/api/formBuilderApi";
import { ui } from "@patient-watch/ui";
import { Label } from "@patient-watch/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@patient-watch/ui/select";
import type { InputT } from "@patient-watch/types";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useBuilderAutoSave } from "./useAutoSave";
import { LogicConditionFields, collectPriorInputs, defaultOperator, defaultRuleValue } from "./LogicConditionFields";

interface PageLogicEditorProps {
    form: FormBuilderData;
    pageId: string;
    pageLogic: unknown;
    onSave: (logic: PageLogic | null) => Promise<unknown>;
}

const defaultLogic = (): PageLogic => ({
    rules: [],
    fallback: { action: "next" },
});

export function PageLogicEditor({ form, pageId, pageLogic, onSave }: PageLogicEditorProps) {
    const [logic, setLogic] = useState(() => parsePageLogic(pageLogic) ?? defaultLogic());

    useEffect(() => {
        setLogic(parsePageLogic(pageLogic) ?? defaultLogic());
    }, [pageId]);

    const allInputs = useMemo(() => form.pages.flatMap((page) => page.inputs as InputT[]), [form]);
    const inputsWithChoices = useInputsWithChoices(allInputs);
    const priorInputs = useMemo(
        () => collectPriorInputs(form, pageId, new Map(inputsWithChoices.map((input) => [input.input_id, input]))),
        [form, pageId, inputsWithChoices]
    );
    const pageOptions = [...form.pages]
        .sort((a, b) => a.page_index - b.page_index)
        .map((page) => ({
            page_id: page.page_id,
            label: page.page_label ?? `Page ${page.page_index}`,
        }));

    const issues = validatePageLogic(
        form.pages.map((page) => ({
            page_id: page.page_id,
            page_index: page.page_index,
            page_logic: page.page_id === pageId ? logic : parsePageLogic(page.page_logic),
            inputs: page.inputs.map((input) => ({
                input_id: input.input_id,
                input_label: input.input_label,
            })),
        }))
    ).filter((issue) => issue.page_id === pageId);

    const autoSave = useBuilderAutoSave(logic, async (next) => {
        const nextIssues = validatePageLogic(
            form.pages.map((page) => ({
                page_id: page.page_id,
                page_index: page.page_index,
                page_logic: page.page_id === pageId ? next : parsePageLogic(page.page_logic),
                inputs: page.inputs.map((input) => ({
                    input_id: input.input_id,
                    input_label: input.input_label,
                })),
            }))
        ).filter((issue) => issue.page_id === pageId);
        if (nextIssues.length > 0) throw new Error(nextIssues[0]?.message ?? "Review this page's logic.");
        const hasContent = next.rules.length > 0 || next.fallback.action !== "next";
        await onSave(hasContent ? next : null);
    });

    const updateLogic = setLogic;

    return (
        <div className="space-y-2 px-1 py-2 border-muted border-t min-w-0" data-testid="page-logic-editor">
            <div className="min-w-0">
                <p className="font-medium text-xs">After this page</p>
                <p className="text-2xs text-muted-foreground break-words text-pretty">
                    Send patients to another page based on their answers.
                </p>
            </div>

            {logic.rules.map((rule, index) => (
                <div key={`${rule.when.input_id}-${index}`} className="space-y-2 bg-muted/40 p-2 rounded-md">
                    <div className="flex justify-between items-center">
                        <Label className="text-2xs">Rule {index + 1}</Label>
                        <ui.Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="w-6 h-6"
                            onClick={() => {
                                updateLogic({
                                    ...logic,
                                    rules: logic.rules.filter((_, ruleIndex) => ruleIndex !== index),
                                });
                            }}
                        >
                            <Trash2 size={14} />
                        </ui.Button>
                    </div>

                    <LogicConditionFields
                        inputs={priorInputs}
                        condition={rule.when}
                        onChange={(when) => {
                            const nextRules = [...logic.rules];
                            nextRules[index] = { ...rule, when };
                            updateLogic({ ...logic, rules: nextRules });
                        }}
                    />

                    <Select
                        value={rule.then.action === "jump" ? rule.then.target_page_id : rule.then.action}
                        onValueChange={(value) => {
                            const nextRules = [...logic.rules];
                            nextRules[index] = {
                                ...rule,
                                // oxlint-disable-next-line -- `then` is the persisted PageLogic schema field.
                                then: value === "end" ? { action: "end" } : { action: "jump", target_page_id: value },
                            };
                            updateLogic({ ...logic, rules: nextRules });
                        }}
                    >
                        <SelectTrigger size="sm" className="h-8 min-h-8 max-h-8 text-xs">
                            <SelectValue placeholder="Then go to" />
                        </SelectTrigger>
                        <SelectContent>
                            {pageOptions.map((page) => (
                                <SelectItem key={page.page_id} value={page.page_id}>
                                    {page.label}
                                </SelectItem>
                            ))}
                            <SelectItem value="end">End form</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            ))}

            <button
                type="button"
                data-testid="page-logic-add-rule"
                disabled={priorInputs.length === 0}
                className="flex items-center gap-1 hover:bg-muted disabled:opacity-40 px-2 py-1 rounded-md w-full min-w-0 text-muted-foreground hover:text-foreground text-xs text-left transition-colors disabled:pointer-events-none"
                onClick={() => {
                    const firstInput = priorInputs[0];
                    if (!firstInput) return;
                    updateLogic({
                        ...logic,
                        rules: [
                            ...logic.rules,
                            {
                                when: {
                                    input_id: firstInput.input_id,
                                    operator: defaultOperator(firstInput.input_key),
                                    value: defaultRuleValue(firstInput.input_key),
                                },
                                // oxlint-disable-next-line -- `then` is the persisted PageLogic schema field.
                                then: { action: "end" },
                            },
                        ],
                    });
                }}
            >
                <Plus size={14} className="shrink-0" /> Add rule
            </button>

            {logic.rules.length > 0 ? (
                <div className="space-y-1.5 min-w-0">
                    <Label className="text-2xs">Otherwise</Label>
                    <Select
                        value={logic.fallback.action === "jump" ? logic.fallback.target_page_id : logic.fallback.action}
                        onValueChange={(value) => {
                            updateLogic({
                                ...logic,
                                fallback:
                                    value === "next" || value === "end"
                                        ? { action: value }
                                        : { action: "jump", target_page_id: value },
                            });
                        }}
                    >
                        <SelectTrigger size="sm" className="h-8 min-h-8 max-h-8 text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="next">Go to next page</SelectItem>
                            {pageOptions.map((page) => (
                                <SelectItem key={page.page_id} value={page.page_id}>
                                    {page.label}
                                </SelectItem>
                            ))}
                            <SelectItem value="end">End form</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            ) : null}

            {issues.length > 0 ? <p className="text-2xs text-destructive">{issues[0]?.message}</p> : null}
            <div className="flex justify-between items-center gap-2 text-2xs text-muted-foreground" aria-live="polite">
                <span>
                    {autoSave.status === "saving"
                        ? "Saving logic…"
                        : autoSave.status === "unsaved"
                          ? "Logic not saved"
                          : autoSave.status === "error"
                            ? "Logic save failed"
                            : "Logic saved"}
                </span>
                {autoSave.status === "error" ? (
                    <ui.Button type="button" variant="ghost" size="sm" onClick={autoSave.retry}>
                        Retry
                    </ui.Button>
                ) : null}
            </div>
        </div>
    );
}
